import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatsBand } from "@/components/StatsBand";
import { FAQSection } from "@/components/FAQSection";
import { ASSETS } from "@/data/content";
import { SOLUTION_PAGE, SOLUTION_FAQ } from "@/data/solutionContent";

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setOn(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setOn(true), obs.disconnect())),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${on ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default function SolutionPage() {
  const S = SOLUTION_PAGE;
  return (
    <div data-testid="solution-page" className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section data-testid="solution-hero" className="relative overflow-hidden bg-[#0b0b0c] hero-noise">
          <img src={S.hero.bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.86) 0%, rgba(2,2,2,0.62) 50%, rgba(2,2,2,0.86) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[480px] md:min-h-[520px] flex items-center justify-center pt-32 pb-16 md:pt-36 md:pb-20">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                {S.hero.eyebrow}
              </p>
              <h1
                data-testid="solution-hero-title"
                className="font-nunito font-extrabold text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight"
              >
                {S.hero.titlePre}
                <span className="text-[#ee2e24]">{S.hero.titleAccent}</span>
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {S.hero.sub}
              </p>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section data-testid="solution-intro" className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="font-nunito text-2xl md:text-[1.9rem] font-extrabold text-[#383838] leading-snug heading-redul">
              {S.intro.heading}
            </h2>
            <p className="mt-6 text-[15px] text-[#6e6e6e] leading-relaxed">{S.intro.body}</p>
          </div>
        </section>

        {/* Five elements */}
        <section data-testid="solution-elements" className="bg-[#fafafa] border-y border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
                {S.elements.eyebrow}
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul-center">
                {S.elements.heading}
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {S.elements.items.map((el, i) => {
                const Icon = el.icon;
                return (
                  <Reveal key={el.title} delay={i * 70}>
                    <div
                      data-testid={`solution-element-card-${i + 1}`}
                      className="relative h-full rounded-[12px] border border-[#ececec] bg-white p-6 shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
                    >
                      <span className="absolute top-4 right-4 font-nunito text-3xl font-extrabold text-[#ee2e24]/[0.12]">
                        {el.letter}
                      </span>
                      <div className="h-11 w-11 rounded-[10px] bg-[#ee2e24]/[0.08] flex items-center justify-center">
                        <Icon size={20} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </div>
                      <h3 className="mt-4 font-nunito text-[15px] font-extrabold text-[#383838]">{el.title}</h3>
                      <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed">{el.line}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Powerful / Effective / Proven */}
        <section data-testid="solution-pillars" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            {S.pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 100}>
                  <div data-testid={`solution-pillar-${i + 1}`}>
                    <div className="flex items-center gap-3">
                      <span className="h-10 w-10 rounded-[10px] bg-[#ee2e24]/[0.08] flex items-center justify-center">
                        <Icon size={18} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </span>
                      <h3 className="font-nunito text-lg font-extrabold text-[#383838]">{p.title}</h3>
                    </div>
                    <p className="mt-4 text-[14px] text-[#6e6e6e] leading-relaxed">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* Stats + Awards */}
        <StatsBand />
        <section data-testid="solution-awards" className="bg-white py-14 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#9a9a9a] mb-6">
              Awards &amp; Recognition
            </p>
            <img src={ASSETS.awardsStrip} alt="Slingshot SEO awards and recognition" className="mx-auto w-full max-w-4xl h-auto" loading="lazy" />
          </div>
        </section>

        {/* FAQ + schema */}
        <FAQSection heading={SOLUTION_FAQ.heading} items={SOLUTION_FAQ.items} injectSchema />

        {/* CTA */}
        <section data-testid="solution-cta" className="bg-[#fafafa] border-t border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">{S.cta.heading}</h2>
            <p className="mt-4 text-[15px] text-[#6e6e6e] leading-relaxed">{S.cta.sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/#visibility-audit"
                data-testid="solution-cta-primary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
              >
                {S.cta.primaryText}
              </Link>
              <a
                href={S.cta.secondaryHref}
                data-testid="solution-cta-secondary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] border border-[#d9d9d9] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#383838] hover:border-[#ee2e24] hover:text-[#ee2e24] transition-colors duration-150"
              >
                {S.cta.secondaryText}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
